// AGC: DevToolsAdmin - Developer tools configuration for admin panel
// AGC: Includes Eruda mobile console toggle and other dev settings

import React, { useState, useEffect } from 'react'
import { useTranslation } from '../../hooks'

// AGC: Storage keys for dev tool settings
const ERUDA_STORAGE_KEY = 'droidfarm-eruda-enabled'

/**
 * AGC: DevToolsAdmin - Configure developer tools
 * AGC: Features:
 * AGC: - Toggle Eruda mobile console on/off
 * AGC: - Settings persist in localStorage
 * AGC: - Requires page refresh to apply changes
 */
export const DevToolsAdmin: React.FC = () => {
  const { t } = useTranslation()
  
  // AGC: Eruda enabled state
  const [erudaEnabled, setErudaEnabled] = useState<boolean>(false)
  
  // AGC: Track if setting was changed (to show refresh message)
  const [settingChanged, setSettingChanged] = useState<boolean>(false)
  
  // AGC: Load current setting from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(ERUDA_STORAGE_KEY)
      setErudaEnabled(stored === 'true')
    } catch (e) {
      console.error('AGC: Failed to read Eruda setting from localStorage:', e)
    }
  }, [])
  
  // AGC: Handle toggle change
  const handleErudaToggle = () => {
    const newValue = !erudaEnabled
    setErudaEnabled(newValue)
    setSettingChanged(true)
    
    try {
      if (newValue) {
        localStorage.setItem(ERUDA_STORAGE_KEY, 'true')
      } else {
        localStorage.removeItem(ERUDA_STORAGE_KEY)
      }
      console.log(`AGC: Eruda setting updated to: ${newValue}`)
    } catch (e) {
      console.error('AGC: Failed to save Eruda setting to localStorage:', e)
    }
  }
  
  // AGC: Handle page refresh
  const handleRefresh = () => {
    window.location.reload()
  }
  
  return (
    <div className="space-y-6">
      {/* AGC: Header */}
      <div className="bg-white dark:bg-admin-card-dark rounded-xl shadow-card dark:shadow-none p-6 border border-slate-200 dark:border-white/5">
        <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-200 mb-2">
          🛠️ {t('admin.devTools.title')}
        </h2>
        <p className="text-slate-600 dark:text-slate-300">
          {t('admin.devTools.description')}
        </p>
      </div>
      
      {/* AGC: Refresh required notification */}
      {settingChanged && (
        <div className="bg-warning-50 dark:bg-yellow-500/15 border border-slate-200 dark:border-white/5 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">⚠️</span>
            <div>
              <p className="font-medium text-warning-700 dark:text-yellow-400">
                {t('admin.devTools.refreshRequired')}
              </p>
              <p className="text-sm text-warning-700 dark:text-yellow-400">
                {t('admin.devTools.refreshDescription')}
              </p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors"
          >
            {t('admin.devTools.refreshNow')}
          </button>
        </div>
      )}
      
      {/* AGC: Eruda Toggle */}
      <div className="bg-white dark:bg-admin-card-dark rounded-xl shadow-card dark:shadow-none p-6 border border-slate-200 dark:border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl">🔧</span>
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 dark:text-slate-200">
                {t('admin.devTools.eruda.title')}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {t('admin.devTools.eruda.description')}
              </p>
            </div>
          </div>
          
          {/* AGC: Toggle switch */}
          <button
            onClick={handleErudaToggle}
            className={`
              relative inline-flex h-8 w-14 items-center rounded-full transition-colors
              ${erudaEnabled 
                ? 'bg-green-500' 
                : 'bg-gray-300'
              }
            `}
            role="switch"
            aria-checked={erudaEnabled}
          >
            <span
              className={`
                inline-block h-6 w-6 transform rounded-full bg-white shadow-md transition-transform
                ${erudaEnabled ? 'translate-x-7' : 'translate-x-1'}
              `}
            />
          </button>
        </div>
        
        {/* AGC: Current status indicator */}
        <div className="mt-4 pt-4 border-t border-slate-100 dark:border-white/5">
          <div className="flex items-center gap-2 text-sm">
            <span className={`w-2 h-2 rounded-full ${erudaEnabled ? 'bg-brand-primary dark:bg-brand-light' : 'bg-slate-400 dark:bg-slate-500'}`} />
            <span className="text-slate-600 dark:text-slate-300">
              {t('common.status')}: {' '}
              <span className={erudaEnabled ? 'text-brand-primary dark:text-emerald-400 font-medium' : 'text-slate-500 dark:text-slate-400'}>
                {erudaEnabled ? t('common.enabled') : t('common.disabled')}
              </span>
            </span>
          </div>
        </div>

        {/* AGC: Info about Eruda */}
        <div className="mt-4 p-4 bg-info-50 dark:bg-blue-500/15 rounded-lg">
          <p className="text-sm text-info-700 dark:text-blue-400">
            <span className="font-medium">ℹ️ {t('admin.devTools.eruda.info')}</span>
          </p>
          <ul className="mt-2 space-y-1 text-sm text-info-700 dark:text-blue-400">
            <li>• {t('admin.devTools.eruda.feature1')}</li>
            <li>• {t('admin.devTools.eruda.feature2')}</li>
            <li>• {t('admin.devTools.eruda.feature3')}</li>
          </ul>
        </div>
      </div>
      
      {/* AGC: Warning about performance */}
      {erudaEnabled && (
        <div className="bg-error-50 dark:bg-red-500/15 border border-slate-200 dark:border-white/5 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <span className="text-xl">⚡</span>
            <div>
              <p className="font-medium text-error-700 dark:text-red-400">
                {t('admin.devTools.performanceWarning.title')}
              </p>
              <p className="text-sm text-error-700 dark:text-red-400 mt-1">
                {t('admin.devTools.performanceWarning.description')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DevToolsAdmin
